import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import Logo from '../assets/favicon.jpeg';
import './LegalTheme.css';

const css = `
  *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }

  :root {
    --orange: #F1561D;
    --green: #0cb44aff;
    --orange-dark: #C94414;
    --orange-light: #FF7A45;
    --orange-pale: #FFF0EB;
    --black: #0D0D0D;
    --dark: #1A1A1A;
    --grey: #6B6B6B;
    --light-grey: #F5F4F2;
    --white: #FFFFFF;
    --font-display: 'Syne', sans-serif;
    --font-body: 'DM Sans', sans-serif;
  }

  html { scroll-behavior: smooth; }
  body { font-family: var(--font-body); background: var(--white); color: var(--black); overflow-x: hidden; }

  nav {
    position: fixed; top: 0; left: 0; right: 0; z-index: 100;
    display: flex; align-items: center; justify-content: space-between;
    padding: 16px 48px;
    background: rgba(255,255,255,0.92);
    backdrop-filter: blur(12px);
    border-bottom: 1px solid rgba(0,0,0,0.06);
    gap: 12px;
  }

  .nav-logo {
    display: flex; align-items: center; gap: 10px;
    font-family: var(--font-display); font-size: 18px; font-weight: 800;
    color: var(--black); text-decoration: none;
    white-space: nowrap; flex-shrink: 0;
  }

  .nav-back {
    background: transparent; color: var(--black);
    border: 1.5px solid rgba(0,0,0,.15); border-radius: 100px;
    padding: 10px 20px; font-size: 13px; font-weight: 600; cursor: pointer;
    font-family: var(--font-body); transition: all .2s;
    display: inline-flex; align-items: center; gap: 6px;
    text-decoration: none;
  }
  .nav-back:hover { border-color: var(--orange); color: var(--orange); }

  .page-wrap {
    max-width: 760px;
    margin: 0 auto;
    padding: 136px 24px 100px;
  }

  .page-icon {
    display: flex;
    justify-content: center;
    margin-bottom: 28px;
  }

  .page-title {
    font-family: var(--font-display);
    font-size: clamp(32px, 5vw, 48px);
    font-weight: 800;
    color: var(--black);
    text-align: center;
    line-height: 1.1;
    letter-spacing: -.02em;
    margin-bottom: 8px;
  }

  .page-date {
    font-size: 13px;
    color: var(--grey);
    text-align: center;
    margin-bottom: 48px;
  }

  .page-divider {
    height: 1px;
    background: rgba(0,0,0,.08);
    margin-bottom: 48px;
  }

  .policy-section {
    margin-bottom: 36px;
  }

  .policy-section-title {
    font-family: var(--font-display);
    font-size: 18px;
    font-weight: 700;
    color: var(--orange);
    margin-bottom: 12px;
  }

  .policy-section-body {
    font-size: 15px;
    font-weight: 300;
    color: #444;
    line-height: 1.8;
  }

  .policy-bullets {
    background: var(--light-grey);
    border-radius: 16px;
    padding: 20px 24px;
    margin-top: 16px;
    display: flex;
    flex-direction: column;
    gap: 12px;
  }

  .policy-bullet {
    display: flex;
    gap: 12px;
    align-items: flex-start;
  }

  .policy-bullet-dot {
    color: var(--orange);
    font-weight: 700;
    font-size: 16px;
    flex-shrink: 0;
    line-height: 1.6;
  }

  .policy-bullet-text {
    font-size: 14px;
    color: #333;
    line-height: 1.7;
  }

  .policy-sub-label {
    font-weight: 600;
    font-size: 14px;
    color: var(--black);
    margin: 18px 0 10px;
  }

  .contact-link {
    color: var(--orange);
    text-decoration: none;
    font-weight: 500;
  }
  .contact-link:hover { text-decoration: underline; }

  footer {
    background: var(--light-grey);
    padding: 40px 48px;
    border-top: 1px solid rgba(0,0,0,.06);
    text-align: center;
  }
  .footer-copy { font-size: 13px; color: var(--grey); }
  .footer-links-row {
    display: flex; gap: 24px; justify-content: center; margin-bottom: 16px; flex-wrap: wrap;
  }
  .footer-links-row a {
    font-size: 13px; color: var(--grey); text-decoration: none; transition: color .2s;
  }
  .footer-links-row a:hover { color: var(--orange); }

  @keyframes fade-up { from { opacity: 0; transform: translateY(20px); } to { opacity: 1; transform: translateY(0); } }
  .animate-in { animation: fade-up .5s ease both; }
  .animate-in-delay { animation: fade-up .5s .1s ease both; }

  @media (max-width: 600px) {
    nav { padding: 12px 16px; }
    .nav-logo { font-size: 15px; }
    .nav-logo img { width: 30px !important; height: 30px !important; }
    .page-wrap { padding: 110px 16px 80px; }
    footer { padding: 32px 16px; }
    .policy-bullets { padding: 16px; }
  }
`;

const PrivacyPage: React.FC = () => {
  const navigate = useNavigate();

  useEffect(() => {
    window.scrollTo(0, 0);

    const link = document.createElement('link');
    link.href = 'https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700&family=Manrope:wght@600;700;800&display=swap';
    link.rel = 'stylesheet';
    document.head.appendChild(link);

    return () => {
      document.head.removeChild(link);
    };
  }, []);

  return (
    <div className="legal-page">
      <Helmet>
        <title>Privacy Policy — JAC ScanPayGo</title>
        <meta name="description" content="How JAC ScanPayGo collects, uses, and protects your personal data." />
        <link rel="canonical" href="https://www.jacscanpaygo.co.za/privacy" />
      </Helmet>

      <style>{css}</style>

      {/* ── NAV ── */}
      <nav>
        <a href="/" className="nav-logo">
          <img
            src={Logo}
            alt="Logo"
            style={{ width: 36, height: 36, borderRadius: 10, objectFit: 'cover' }}
          />
          <span>JAC <span className="brand-accent">ScanPayGo</span></span>
        </a>
        <button className="nav-back" onClick={() => navigate(-1)}>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
            <path d="M19 12H5M12 5l-7 7 7 7" />
          </svg>
          Back
        </button>
      </nav>

      {/* ── CONTENT ── */}
      <div className="page-wrap">

        <div className="page-icon animate-in">
          <svg width="72" height="72" viewBox="0 0 72 72" fill="none">
            <circle cx="36" cy="36" r="36" fill="#FFF0EB" />
            <path d="M36 16L22 22v14c0 9.6 5.8 18.6 14 21.6C44.2 54.6 50 45.6 50 36V22L36 16z" fill="#F1561D" opacity=".2" />
            <path d="M36 16L22 22v14c0 9.6 5.8 18.6 14 21.6C44.2 54.6 50 45.6 50 36V22L36 16z" stroke="#F1561D" strokeWidth="2" strokeLinejoin="round" />
            <path d="M30 36l4 4 8-8" stroke="#F1561D" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </div>

        <h1 className="page-title animate-in">Privacy &amp; Data Protection</h1>
        <div className="page-date animate-in-delay">Last Updated: December 2025</div>
        <div className="page-divider" />

        <div className="policy-section">
          <div className="policy-section-title">1. Introduction</div>
          <div className="policy-section-body">
            We are committed to protecting your personal information and your right to privacy. JAC ScanPayGo is a mobile self-checkout platform designed for South African retail. If you have any questions or concerns about our policy, or our practices with regards to your personal information, please contact us at{' '}
            <a href="mailto:support@jacscanpaygo.co.za" className="contact-link">support@jacscanpaygo.co.za</a>.
          </div>
        </div>

        <div className="policy-section">
          <div className="policy-section-title">2. Photo Verification &amp; Security</div>
          <div className="policy-section-body">
            To help prevent errors, losses, and shoplifting, this app requires a photo verification at checkout. By using this service, you acknowledge the following usage of your data:
          </div>
          <div className="policy-bullets">
            {[
              'Your photo is taken only to verify a shopping session if a payment or stock discrepancy is identified.',
              'Your photo is not monitored in real time and is not used for automated decision-making.',
              'Photos are accessed manually and only if a discrepancy or loss investigation is required.',
              'A new photo is required for each shopping session and is treated independently from previous visits.',
            ].map((text, i) => (
              <div className="policy-bullet" key={i}>
                <span className="policy-bullet-dot">•</span>
                <span className="policy-bullet-text">{text}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="policy-section">
          <div className="policy-section-title">3. Data Management &amp; Transparency</div>
          <div className="policy-section-body">
            Your data collected through photo verification is handled securely and confidentially.
          </div>
          <div className="policy-sub-label">Retention Policy:</div>
          <div className="policy-bullets">
            {[
              'Photo verification data is retained for a limited period to support loss investigation and then securely deleted.',
              'All personal data is stored in compliance with applicable data protection regulations.',
              'Users may request data deletion or access by contacting support.',
            ].map((text, i) => (
              <div className="policy-bullet" key={i}>
                <span className="policy-bullet-dot">•</span>
                <span className="policy-bullet-text">{text}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="policy-section">
          <div className="policy-section-title">4. Information We Collect</div>
          <div className="policy-section-body">
            When you use JAC ScanPayGo, we may collect the following categories of information:
          </div>
          <div className="policy-bullets">
            {[
              'Account information: name, email address, and password (stored encrypted).',
              'Location data: GPS coordinates used to verify store proximity at checkout. Not stored persistently.',
              'Transaction data: items scanned, cart totals, payment references, and timestamps.',
              'Device information: device type and operating system for compatibility purposes.',
            ].map((text, i) => (
              <div className="policy-bullet" key={i}>
                <span className="policy-bullet-dot">•</span>
                <span className="policy-bullet-text">{text}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="policy-section">
          <div className="policy-section-title">5. How We Use Your Information</div>
          <div className="policy-section-body">
            The information we collect is used solely to operate, maintain, and improve the JAC ScanPayGo service. We do not sell your personal data to third parties. Specifically, your data is used to:
          </div>
          <div className="policy-bullets">
            {[
              'Authenticate your account and verify your identity at checkout.',
              'Process payments securely via Paystack, our payment provider.',
              'Generate transaction records and receipts.',
              'Investigate stock discrepancies or payment disputes when they arise.',
              'Provide store managers with aggregate, anonymised sales analytics.',
            ].map((text, i) => (
              <div className="policy-bullet" key={i}>
                <span className="policy-bullet-dot">•</span>
                <span className="policy-bullet-text">{text}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="policy-section">
          <div className="policy-section-title">6. Third-Party Services</div>
          <div className="policy-section-body">
            JAC ScanPayGo integrates with the following third-party services, each governed by their own privacy policies:
          </div>
          <div className="policy-bullets">
            {[
              'Paystack — payment processing. Your card details are handled directly by Paystack and are never stored on our servers.',
              'Google Play Store — app distribution and in-app review collection.',
              'Render / cloud infrastructure — server hosting for our backend API and admin dashboard.',
            ].map((text, i) => (
              <div className="policy-bullet" key={i}>
                <span className="policy-bullet-dot">•</span>
                <span className="policy-bullet-text">{text}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="policy-section">
          <div className="policy-section-title">7. User Rights</div>
          <div className="policy-section-body">
            You have the following rights regarding your personal data. To exercise any of them, contact us at{' '}
            <a href="mailto:support@jacscanpaygo.co.za" className="contact-link">support@jacscanpaygo.co.za</a>:
          </div>
          <div className="policy-bullets">
            {[
              'Right of access — request a copy of the personal data we hold about you.',
              'Right to rectification — request correction of inaccurate or incomplete data.',
              'Right to erasure — request deletion of your account and associated data.',
              'Right to object — object to certain types of processing, such as marketing.',
              'Right to data portability — request your data in a structured, machine-readable format.',
            ].map((text, i) => (
              <div className="policy-bullet" key={i}>
                <span className="policy-bullet-dot">•</span>
                <span className="policy-bullet-text">{text}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="policy-section">
          <div className="policy-section-title">8. Data Security</div>
          <div className="policy-section-body">
            We implement industry-standard security measures to protect your data, including encrypted data transmission (HTTPS), hashed password storage, rate limiting on authentication endpoints, and audit logging for administrative actions. Despite these measures, no system is completely immune to security risks and we encourage you to use a strong, unique password.
          </div>
        </div>

        <div className="policy-section">
          <div className="policy-section-title">9. Changes to This Policy</div>
          <div className="policy-section-body">
            We may update this Privacy Policy from time to time. When we do, we will revise the "Last Updated" date at the top of this page. Continued use of JAC ScanPayGo after any changes constitutes your acceptance of the revised policy.
          </div>
        </div>

        <div className="policy-section">
          <div className="policy-section-title">10. Contact Information</div>
          <div className="policy-section-body">
            For any privacy-related questions, data requests, or concerns, please contact us at{' '}
            <a href="mailto:support@jacscanpaygo.co.za" className="contact-link">support@jacscanpaygo.co.za</a>. We aim to respond to all requests within 5 business days.
          </div>
        </div>

      </div>

      {/* ── FOOTER ── */}
      <footer>
        <div className="footer-links-row">
          <a href="/privacy">Privacy Policy</a>
          <a href="/terms">Terms of Service</a>
          <a href="mailto:support@jacscanpaygo.co.za">support@jacscanpaygo.co.za</a>
          <a href="/">Home</a>
        </div>
        <div className="footer-copy">© {new Date().getFullYear()} JAC ScanPayGo. All rights reserved.</div>
      </footer>
    </div>
  );
};

export default PrivacyPage;
